/**
 * Thin composer — delegates every card to a focused component under
 * `components/dashboard/`. Phase B.6 refactor: 561 LOC → ~85 LOC.
 */
import React, { useEffect, useState } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import CreateWeddingModal from '../components/CreateWeddingModal';

import EmptyWeddingState from '../components/dashboard/EmptyWeddingState';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import CountdownCards from '../components/dashboard/CountdownCards';
import BudgetSummaryCard from '../components/dashboard/BudgetSummaryCard';
import TasksSummaryCard from '../components/dashboard/TasksSummaryCard';
import CategoryBreakdownCard from '../components/dashboard/CategoryBreakdownCard';
import { VendorsStatCard, GuestsStatCard } from '../components/dashboard/StatCards';
import EventsSummaryCard from '../components/dashboard/EventsSummaryCard';
import UpcomingPaymentsCard from '../components/dashboard/UpcomingPaymentsCard';
import Next30DaysCard from '../components/dashboard/Next30DaysCard';
import BudgetVsActualCard from '../components/dashboard/BudgetVsActualCard';
import ActivityFeedCard from '../components/dashboard/ActivityFeedCard';
import SpendingBenchmarksCard from '../components/dashboard/SpendingBenchmarksCard';
import EventBenchmarksCard from '../components/dashboard/EventBenchmarksCard';
import PartnerInviteCard from '../components/dashboard/PartnerInviteCard';
import { useBenchmarks } from '../components/dashboard/useBenchmarks';

export default function DashboardPage() {
  const { user } = useAuth();
  const {
    currentWedding, dashboard, loading, weddings,
    fetchWeddings, fetchDashboard, fetchCategories, fetchCurrencies,
  } = useWedding();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { benchmarkData, selectedEvent, setSelectedEvent } = useBenchmarks(currentWedding?.id);

  useEffect(() => {
    fetchWeddings();
    fetchCategories();
    fetchCurrencies();
  }, [fetchWeddings, fetchCategories, fetchCurrencies]);

  useEffect(() => {
    if (currentWedding) fetchDashboard(currentWedding.id);
  }, [currentWedding, fetchDashboard]);

  if (!currentWedding && weddings.length === 0) {
    return (
      <Layout>
        <EmptyWeddingState userName={user?.name} onCreate={() => setShowCreateModal(true)} />
        <CreateWeddingModal open={showCreateModal} onClose={() => setShowCreateModal(false)} />
      </Layout>
    );
  }

  const currency = currentWedding?.preferred_currency;

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        <DashboardHeader wedding={currentWedding} />

        {/* Partner-invite banner — only renders when planning_scope === 'self'.
            On accept the wedding flips to 'both' and this disappears. */}
        <PartnerInviteCard
          wedding={currentWedding}
          onScopeFlipped={() => fetchWeddings()}
        />

        {loading ? (
          <div className="text-center py-12 text-muted">Loading dashboard...</div>
        ) : (
          <>
            <CountdownCards countdown={dashboard?.countdown} />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <BudgetSummaryCard summary={dashboard?.budget_summary} currency={currency} />
              <TasksSummaryCard summary={dashboard?.checklist_summary} />
              <CategoryBreakdownCard categoryBreakdown={dashboard?.category_breakdown} currency={currency} />
              <VendorsStatCard summary={dashboard?.vendor_summary} currency={currency} />
              <GuestsStatCard summary={dashboard?.guest_summary} />
              <EventsSummaryCard events={dashboard?.event_summaries} currency={currency} />
            </div>

            <BudgetVsActualCard data={dashboard?.budget_trajectory} currency={currency} />
            <Next30DaysCard data={dashboard?.next_30_days} currency={currency} />
            <UpcomingPaymentsCard reminders={dashboard?.payment_reminders} currency={currency} />
            <ActivityFeedCard weddingId={currentWedding?.id} />
            <SpendingBenchmarksCard data={benchmarkData} />
            <EventBenchmarksCard
              data={benchmarkData}
              selectedEvent={selectedEvent}
              onSelect={setSelectedEvent}
            />
          </>
        )}
      </div>
      <CreateWeddingModal open={showCreateModal} onClose={() => setShowCreateModal(false)} />
    </Layout>
  );
}
